import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { auth, HttpError } from '../api/client';
import { Alert, Button, Field, Input } from '../components/ui';
import BrandLogo from '../components/BrandLogo';

/**
 * Public reset landing for the emailed link (/reset-password?token=…).
 * The token is single-use and expires after 1 hour; a missing, invalid,
 * or expired token lands on a clear dead-end state with a path back to
 * requesting a fresh link from the sign-in page.
 */
export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [linkInvalid, setLinkInvalid] = useState(!token);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (password.length < 8) next.password = 'Password must be at least 8 characters.';
    if (!confirm) next.confirm = 'Re-enter your new password.';
    else if (confirm !== password) next.confirm = 'Passwords do not match.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setBusy(true);
    setServerError(null);
    try {
      await auth.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      if (err instanceof HttpError && err.status === 400) {
        // Invalid or expired token — same dead-end state as a bad link.
        setLinkInvalid(true);
      } else if (err instanceof HttpError) {
        setServerError(err.message);
      } else {
        setServerError('Could not reach the server. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--s5)' }}>
          <BrandLogo variant="color" height={44} />
        </div>

        {done ? (
          <>
            <h1>Password updated</h1>
            <p className="auth-sub">
              Your password has been reset. You can sign in with your new password now.
            </p>
            <Alert tone="success">You&apos;re all set — welcome back.</Alert>
            <div className="form-actions" style={{ marginTop: 'var(--s5)' }}>
              <Link className="btn btn-primary btn-block" to="/login">
                Continue to sign in
              </Link>
            </div>
          </>
        ) : linkInvalid ? (
          <>
            <h1>This reset link isn&apos;t valid</h1>
            <p className="auth-sub">
              The link may have expired (reset links last 1 hour), already been used,
              or been copied incompletely.
            </p>
            <Alert tone="warning">
              Request a fresh link from the sign-in page — choose{' '}
              <strong>Forgot password?</strong> under the password field.
            </Alert>
            <div className="form-actions" style={{ marginTop: 'var(--s5)' }}>
              <Link className="btn btn-primary btn-block" to="/login">
                Request a new reset link
              </Link>
            </div>
          </>
        ) : (
          <>
            <h1>Set a new password</h1>
            <p className="auth-sub">Choose a new password for your Navigator account.</p>
            {serverError && <Alert tone="error">{serverError}</Alert>}
            <form onSubmit={submit} noValidate>
              <Field label="New password" required hint="At least 8 characters." error={errors.password}>
                <Input
                  id="rp-password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="Create a new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={!!errors.password}
                />
              </Field>
              <Field label="Confirm new password" required error={errors.confirm}>
                <Input
                  id="rp-confirm"
                  type="password"
                  autoComplete="new-password"
                  placeholder="Re-enter your new password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  aria-invalid={!!errors.confirm}
                />
              </Field>
              <Button type="submit" variant="primary" size="lg" block disabled={busy}>
                {busy ? 'Updating…' : 'Update password'}
              </Button>
            </form>
            <p className="text-sm text-muted" style={{ marginTop: 'var(--s5)', textAlign: 'center' }}>
              Remembered it after all? <Link to="/login">Back to sign in</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
